export type UploadErrorReason = 'NETWORK' | 'TIMEOUT' | 'FORBIDDEN' | 'UNKNOWN';

/**
 * S3 업로드 실패 시 에러 원인을 분류하여 전달하는 커스텀 에러
 *
 * - NETWORK: 네트워크 끊김 또는 CORS 차단
 * - TIMEOUT: 업로드 시간 초과
 * - FORBIDDEN: Presigned URL 만료 또는 서명 불일치 (403)
 * - UNKNOWN: 기타 에러
 */
export class S3UploadError extends Error {
  reason: UploadErrorReason;
  /**
   * 네트워크성 실패 시 수집한 사후 진단 한 줄 요약.
   * 예: `probe=PROBE_OK, progress=0B, elapsed=380ms, online=true, host=...`
   */
  diagnostics?: string;

  constructor(
    message: string,
    reason: UploadErrorReason,
    options: { diagnostics?: string } = {},
  ) {
    super(message);
    this.name = 'S3UploadError';
    this.reason = reason;
    this.diagnostics = options.diagnostics;
    Object.setPrototypeOf(this, S3UploadError.prototype);
  }
}

/**
 * axios 에러에서 UploadErrorReason을 추출합니다.
 */
export function classifyAxiosError(error: unknown): UploadErrorReason {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code?: string }).code === 'string'
  ) {
    const code = (error as { code: string }).code;
    if (code === 'ERR_NETWORK') return 'NETWORK';
    if (code === 'ECONNABORTED') return 'TIMEOUT';
  }
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 403
  ) {
    return 'FORBIDDEN';
  }
  return 'UNKNOWN';
}
